import * as React from 'react';
import styles from './BundleContains.scss';
import equal from 'fast-deep-equal/react';
import Contains, { IContainsItem } from '~/components/ItemContains/Contains';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { items as ITEMS_TYPES } from '@wg/wows-entities/const';
import { isEnabledPortForItemInsideBundle, openPortByItem } from '~/settings/port';
import { t } from '~/utils/localization';
import { DivTooltip } from '@wg/wows-react-uikit';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import { State } from '~/Reducers';
import { useSelector } from 'react-redux';
import Account from '~/account/Account';
import classNames from 'classnames';
import store from '~/Store';
import { changeVisiblePopup } from '~/Actions/ActionApp';
import { POPUPS_NAME } from '~/components/PopupManager';
import { isAvailableAdditionalContentForType } from '../helpers';
import { getSectionName, ManagerData } from '~/core/ManagerData';

interface IBundleContains {
    bundle: IBundle;
    className?: string;
    title?: string;
    titleClassName?: string;
    limit?: number;
}

export const prepareItems = (bundle: IBundle, inventory: InventoryState): IContainsItem[] => {
    return bundle.entitlements.map((item, index) => {
        let content;

        const [isExistsItemInInventory, inventoryCount] = Account.getInventoryInfoByType(item.type, item.identifier, item.customisation?.shipId);

        if (item.customTitle) {
            content = (
                <DivTooltip tooltipBody={<DefaultTooltip text={item.customDescription || item.customTitle} />}>
                    <div dangerouslySetInnerHTML={{ __html: item.customTitle }} />
                </DivTooltip>
            );
        } else {
            const isAvailableAdditionalContent = !item.isPrimary && isAvailableAdditionalContentForType(item.type, item.identifier);
            const data = ManagerData.getData(getSectionName(item.type), item.identifier, true);
            content = (
                <div
                    className={classNames({
                        [styles.WoWSEntityWrapper]: isAvailableAdditionalContent,
                        [styles.gold]: (item.type !== ITEMS_TYPES.LOOTBOX && data?.isPremium) || data?.isSpecial || data?.isUnique,
                    })}
                    onClick={() => {
                        if (!isAvailableAdditionalContent) {
                            return;
                        }

                        store.dispatch(
                            changeVisiblePopup(POPUPS_NAME.ITEM_DESCRIPTION_POPUP, true, {
                                item: item,
                                autoDescriptionSettings: bundle.autoDescriptionSettings,
                            }),
                        );
                    }}
                >
                    <WoWSEntity
                        key={`item_${item.identifier}_${index}_${inventory ? 'loaded' : 'not_loaded'}`}
                        type={item.type}
                        id={item.identifier}
                        amount={item.amount}
                        customisation={{
                            ...(item.customisation || {}),
                            inventoryCount: !item.isUnique && inventoryCount,
                            isExistsInInventory: item.isUnique && isExistsItemInInventory,
                        }}
                        presentation={{
                            withText: true,
                            withTooltip: true,
                            iconPosition: 'left',
                            renderWithoutFade: true,
                            hiddenInlineShip: item.type === ITEMS_TYPES.CREWS,
                        }}
                    />
                </div>
            );
        }

        return {
            id: (item.identifier || `${index}_${item.type}`)?.toString(),
            type: item.type as any,
            isBonus: item.isBonus,
            isExistsInInventory: true,
            isPortPreviewEnabled: isEnabledPortForItemInsideBundle(bundle, item),
            portPreviewOnClick: () => {
                openPortByItem({ identifier: item.identifier, type: item.type, shipId: item.customisation?.shipId }, bundle.id);
            },
            content,
        } as IContainsItem;
    });
};

interface IStateSelector {
    accountId: number;
    inventory: InventoryState;
}

const stateSelector = (state: State): IStateSelector => {
    return {
        accountId: state.ReducerAccount.id,
        inventory: state.ReducerAccount.inventory,
    };
};

const BundleContains = ({ bundle, className, title, titleClassName, limit }: IBundleContains) => {
    const state = useSelector<State, IStateSelector>(stateSelector, equal);
    const items = prepareItems(bundle, state.inventory);

    return (
        <div className={className}>
            <Contains items={items} isLarge={true} title={title} titleClassName={titleClassName} limit={limit} />
        </div>
    );
};

export default BundleContains;
